// HELIXIR: floating WhatsApp button (sections/helixir-whatsapp.liquid). Builds the chat link with the page's full
// address and, until the visitor has left an e-mail (data-ask-email), opens the e-mail panel instead of the chat.
// The panel is a modal <dialog>, like the store's login: it takes the focus itself rather than the field, so a phone's
// keyboard does not jump up over it.
// The form posts into a hidden frame (Shopify's hCaptcha still handles it); a blank tab is opened at once, while the
// click still counts as the visitor's, and pointed at the chat once the contact is saved.
if (!customElements.get('helixir-whatsapp')) {
  customElements.define(
    'helixir-whatsapp',
    class HelixirWhatsapp extends HTMLElement {
      static storageKey = 'helixir-whatsapp-contact';

      connectedCallback() {
        this.button = this.querySelector('.hwa__button');
        this.panel = this.querySelector('.hwa__panel');
        this.form = this.querySelector('.hwa__form');
        this.sink = this.querySelector('.hwa__sink');

        const url = this.chatUrl();
        this.button.href = url;
        this.querySelector('.hwa__skip')?.setAttribute('href', url);

        if (!this.panel || !this.hasAttribute('data-ask-email')) return;
        this.button.addEventListener('click', (e) => {
          if (this.known()) return; // already left an e-mail: straight to the chat
          e.preventDefault();
          this.open();
        });
        this.querySelector('.hwa__close').addEventListener('click', () => this.close());
        this.querySelector('.hwa__skip')?.addEventListener('click', () => this.close());
        // Escape: the same slide out as the close button.
        this.panel.addEventListener('cancel', (e) => {
          e.preventDefault();
          this.close();
        });
        // A click on the backdrop lands on the dialog itself, outside its box.
        this.panel.addEventListener('click', (e) => {
          const box = this.panel.getBoundingClientRect();
          const inside =
            e.clientX >= box.left && e.clientX <= box.right && e.clientY >= box.top && e.clientY <= box.bottom;
          if (e.target === this.panel && !inside) this.close();
        });
        // However it closed, the browser has put the focus back on the button.
        this.panel.addEventListener('close', () => {
          this.panel.classList.remove('is-open');
          this.button.setAttribute('aria-expanded', 'false');
        });
        this.form.addEventListener('submit', () => this.send());
      }

      disconnectedCallback() {
        clearTimeout(this.fallback);
      }

      chatUrl() {
        let text = this.dataset.message || '';
        if (this.hasAttribute('data-include-url')) text = `${text.trim()} ${window.location.href}`;
        return `https://api.whatsapp.com/send?phone=${encodeURIComponent(this.dataset.phone)}&text=${encodeURIComponent(text)}`;
      }

      known() {
        try {
          return window.localStorage.getItem(HelixirWhatsapp.storageKey) === '1';
        } catch {
          return false;
        }
      }

      open() {
        if (!this.panel.open) this.panel.showModal();
        this.panel.focus();
        this.button.setAttribute('aria-expanded', 'true');
        requestAnimationFrame(() => this.panel.classList.add('is-open'));
      }

      close() {
        if (!this.panel.open) return;
        this.panel.classList.remove('is-open');
        // Closed once its slide is over, unless it was opened again meanwhile.
        setTimeout(() => {
          if (!this.panel.classList.contains('is-open')) this.panel.close();
        }, 300);
      }

      // The browser has validated the e-mail; the form now posts into the hidden frame. The chat opens once the
      // store has answered, or after a few seconds whatever happens, so the visitor is never left waiting.
      send() {
        try {
          window.localStorage.setItem(HelixirWhatsapp.storageKey, '1');
        } catch {}
        const url = this.chatUrl();
        const tab = window.open('', '_blank');
        let done = false;
        const go = () => {
          if (done) return;
          done = true;
          clearTimeout(this.fallback);
          if (tab && !tab.closed) tab.location.href = url;
          else window.location.href = url;
          this.form.reset();
          this.close();
        };
        this.sink.addEventListener('load', go, { once: true });
        this.fallback = setTimeout(go, 4000);
      }
    }
  );
}
